import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { workoutExecution } from '../../services/api';
import {
  applyQueue, buildSetBody, errorMessage, extraSetIndex, firstOpenIndex, isNetworkError, isPermanentFailure, localDateString,
  mergeSavedSet, newUuid, nextSetIndex, removeSavedSet, safeStorage,
} from './playerLogic';

// O estado do player, do servidor até ao ecrã.
//
//  - O servidor (Bloco 3) é a verdade: o treino em curso, as séries, os estados. Aqui só se guarda o último que se recebeu.
//  - O que o sócio faz regista-se NA HORA no ecrã e vai para uma fila local (guardada no telemóvel); a fila entrega-se ao servidor, uma
//    operação de cada vez e por ordem. Só entram na fila operações que se podem repetir sem efeito duplo: uma série (tem o seu ClientUuid),
//    concluir e saltar um exercício. Substituir, concluir e abandonar o treino exigem ligação e fazem-se depois de a fila esvaziar.
//  - Uma resposta definitiva do servidor (4xx) tira a operação da fila e avisa; um erro de rede ou 5xx deixa-a e tenta outra vez
//    (com espera crescente, e logo que a ligação volta).

const RETRY_STEPS_MS = [2000, 4000, 8000, 15000, 30000];

const queueKey = (executionId) => `queue.${executionId}`;

export default function useWorkoutPlayer({ api = workoutExecution } = {}) {
  const [phase, setPhase] = useState('loading'); // loading | error | today | player | finished
  const [today, setToday] = useState(null);
  const [server, setServer] = useState(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndexState] = useState(0);
  const [finished, setFinished] = useState(null);
  const [error, setError] = useState(null);
  const [sync, setSync] = useState('idle'); // idle | syncing | offline | retry
  const [failedOps, setFailedOps] = useState([]);
  const [busy, setBusy] = useState({});

  const serverRef = useRef(null);
  const queueRef = useRef([]);
  const flushPromise = useRef(null);
  const retryTimer = useRef(null);
  const retryStep = useRef(0);
  const draftUuids = useRef(new Map());
  const startingRef = useRef(false);
  const mounted = useRef(true);
  const flushRef = useRef(null);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; clearTimeout(retryTimer.current); }; }, []);

  const setBusyKey = useCallback((key, value) => { if (mounted.current) setBusy((b) => ({ ...b, [key]: value })); }, []);

  const putServer = useCallback((dto) => { serverRef.current = dto; if (mounted.current) setServer(dto); }, []);

  const putQueue = useCallback((next) => {
    queueRef.current = next;
    if (mounted.current) setQueue(next);
    const id = serverRef.current?.executionId;
    if (id) { if (next.length) safeStorage.set(queueKey(id), next); else safeStorage.remove(queueKey(id)); }
  }, []);

  const setIndex = useCallback((value) => {
    setIndexState((cur) => {
      const next = typeof value === 'function' ? value(cur) : value;
      const id = serverRef.current?.executionId;
      if (id) safeStorage.set(`index.${id}`, next);
      return next;
    });
  }, []);

  // ── a fila ─────────────────────────────────────────────────────────────────

  const run = useCallback(async (op) => {
    const current = serverRef.current;
    if (!current || current.executionId !== op.executionId) return;
    if (op.type === 'set') {
      const res = await api.saveSet(op.executionId, op.exerciseId, op.body);
      putServer(mergeSavedSet(serverRef.current, res.data));
    } else if (op.type === 'complete-exercise') {
      const res = await api.completeExercise(op.executionId, op.exerciseId);
      putServer({ ...res.data, resumed: serverRef.current.resumed });
    } else if (op.type === 'skip-exercise') {
      const res = await api.skipExercise(op.executionId, op.exerciseId, op.reason);
      putServer({ ...res.data, resumed: serverRef.current.resumed });
    }
  }, [api, putServer]);

  const refresh = useCallback(async () => {
    const id = serverRef.current?.executionId;
    if (!id) return;
    try {
      const res = await api.get(id);
      if (res.data.state !== 'InProgress') { putServer(res.data); return; }
      putServer(res.data);
    } catch { /* fica com o que tinha */ }
  }, [api, putServer]);

  const scheduleRetry = useCallback((flushFn) => {
    clearTimeout(retryTimer.current);
    const delay = RETRY_STEPS_MS[Math.min(retryStep.current, RETRY_STEPS_MS.length - 1)];
    retryStep.current += 1;
    retryTimer.current = setTimeout(() => flushFn(), delay);
  }, []);

  const flush = useCallback(() => {
    if (flushPromise.current) return flushPromise.current;
    const loop = (async () => {
      if (queueRef.current.length === 0) { if (mounted.current) setSync('idle'); return true; }
      if (mounted.current) setSync('syncing');
      let needsRefresh = false;
      while (queueRef.current.length > 0) {
        const op = queueRef.current[0];
        try {
          await run(op);
          putQueue(queueRef.current.slice(1));
          retryStep.current = 0;
        } catch (e) {
          if (isPermanentFailure(e)) {
            // O servidor disse que não e repetir não muda nada: sai da fila e o sócio é avisado.
            putQueue(queueRef.current.slice(1));
            if (mounted.current) setFailedOps((f) => [...f, { id: op.uuid || `${op.type}-${op.exerciseId}`, type: op.type, message: errorMessage(e) }]);
            needsRefresh = true;
          } else {
            if (mounted.current) setSync(isNetworkError(e) ? 'offline' : 'retry');
            scheduleRetry(() => { flushPromise.current = null; if (flushRef.current) flushRef.current(); });
            return false;
          }
        }
      }
      if (mounted.current) setSync('idle');
      if (needsRefresh) await refresh();
      return true;
    })().finally(() => { flushPromise.current = null; });
    flushPromise.current = loop;
    return loop;
  }, [putQueue, refresh, run, scheduleRetry]);
  flushRef.current = flush;

  // Voltou a ligação, ou a app voltou ao ecrã: tenta entregar o que ficou.
  useEffect(() => {
    const kick = () => { if (queueRef.current.length > 0) { retryStep.current = 0; flush(); } };
    window.addEventListener('online', kick);
    const onVisible = () => { if (document.visibilityState === 'visible') kick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.removeEventListener('online', kick); document.removeEventListener('visibilitychange', onVisible); };
  }, [flush]);

  // Sair da página com séries por entregar: o navegador avisa.
  useEffect(() => {
    const guard = (e) => { if (queueRef.current.length > 0) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, []);

  // ── entrar e sair do player ────────────────────────────────────────────────

  const enterPlayer = useCallback((dto, show = true) => {
    const id = dto.executionId;
    const savedQueue = safeStorage.get(queueKey(id));
    const savedIndex = safeStorage.get(`index.${id}`);
    serverRef.current = dto;
    setServer(dto);
    const q = Array.isArray(savedQueue) ? savedQueue.filter((op) => op.executionId === id) : [];
    queueRef.current = q;
    setQueue(q);
    const count = dto.exercises.length;
    const fallback = dto.currentIndex ?? Math.max(0, firstOpenIndex(dto.exercises));
    setIndexState(Number.isInteger(savedIndex) && savedIndex >= 0 && savedIndex < count ? savedIndex : fallback);
    safeStorage.set('active', id);
    setPhase(show ? 'player' : 'today');
    if (q.length > 0) flush();
  }, [flush]);

  const clearLocal = useCallback((executionId) => {
    if (!executionId) return;
    safeStorage.remove(queueKey(executionId));
    safeStorage.remove(`index.${executionId}`);
    safeStorage.remove(`rest.${executionId}`);
    if (safeStorage.get('active') === executionId) safeStorage.remove('active');
  }, []);

  const load = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      const res = await api.today(localDateString());
      const dto = res.data;
      // Uma fila deixada por um treino que o servidor já não tem como ativo: entrega-se o que for possível e esquece-se.
      const staleId = safeStorage.get('active');
      if (staleId && staleId !== dto.executionId) {
        const leftovers = safeStorage.get(queueKey(staleId));
        if (Array.isArray(leftovers) && leftovers.length) {
          (async () => {
            let forgotten = true;   // só se esquece o que já não tem salvação
            for (const op of leftovers) {
              try {
                if (op.type === 'set') await api.saveSet(op.executionId, op.exerciseId, op.body);
                else if (op.type === 'complete-exercise') await api.completeExercise(op.executionId, op.exerciseId);
                else if (op.type === 'skip-exercise') await api.skipExercise(op.executionId, op.exerciseId, op.reason);
              } catch (e) {
                // O treino já fechou: o servidor recusa e está certo — mas só se pode apagar a fila
                // quando a recusa é definitiva (400/404/409). Numa falha de rede a fila TEM de ficar:
                // apagá-la aqui perdia séries que o atleta registou e que o servidor nunca recebeu.
                if (!isPermanentFailure(e)) forgotten = false;
              }
            }
            if (forgotten) clearLocal(staleId);
          })();
        } else clearLocal(staleId);
      }
      if (!mounted.current) return;
      if (dto.state === 'InProgress') enterPlayer(dto, false);   // há um treino em curso: o ecrã de hoje oferece "Continuar treino"
      else { serverRef.current = null; setServer(null); setToday(dto); setPhase('today'); }
    } catch (e) {
      if (!mounted.current) return;
      setError(errorMessage(e, 'Não foi possível carregar o treino.'));
      setPhase('error');
    }
  }, [api, clearLocal, enterPlayer]);

  useEffect(() => { load(); }, [load]);

  const start = useCallback(async (dayId) => {
    if (startingRef.current) return false;
    startingRef.current = true;
    setBusyKey('start', true);
    setError(null);
    try {
      const res = await api.start({ workoutDayId: dayId ?? undefined, date: localDateString() });
      enterPlayer({ ...res.data, resumed: res.status === 200 });
      return true;
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível começar o treino.'));
      if (e?.response?.status === 409) load();
      return false;
    } finally {
      startingRef.current = false;
      setBusyKey('start', false);
    }
  }, [api, enterPlayer, load, setBusyKey]);

  // ── o que o sócio faz durante o treino ─────────────────────────────────────

  const view = useMemo(() => applyQueue(server, queue), [server, queue]);

  const enqueue = useCallback((op) => {
    putQueue([...queueRef.current, op]);
    flush();
  }, [flush, putQueue]);

  /** Regista uma série. Aparece logo; o servidor confirma depois. Devolve { ok, errors?, setIndex? }. */
  const logSet = useCallback((exerciseIdx, draft) => {
    const current = applyQueue(serverRef.current, queueRef.current);
    const exercise = current?.exercises[exerciseIdx];
    if (!exercise) return { ok: false, errors: ['Exercício não encontrado.'] };
    if (exercise.status === 'Skipped') return { ok: false, errors: ['Este exercício foi saltado.'] };

    const setIndex = draft.setIndex ?? nextSetIndex(exercise) ?? extraSetIndex(exercise);
    const isWarmup = !!draft.isWarmup;
    if ((exercise.sets || []).some((s) => s.setIndex === setIndex && !!s.isWarmup === isWarmup)) return { ok: false, duplicate: true, setIndex };

    // Um GUID por série (e por exercício): tocar duas vezes no mesmo botão é o mesmo pedido. O aquecimento tem o seu
    // próprio espaço de numeração, por isso o índice entra na chave com o tipo (senão a série de trabalho 1 repetia o
    // pedido do aquecimento 1 e o servidor devolvia-o, sem criar a série).
    const key = `${current.executionId}:${exercise.id}:${isWarmup ? 'w' : 's'}:${setIndex}`;
    if (!draftUuids.current.has(key)) draftUuids.current.set(key, newUuid());
    const { body, errors } = buildSetBody(exercise, setIndex, draft, { clientUuid: draftUuids.current.get(key) });
    if (errors.length) return { ok: false, errors };

    enqueue({ type: 'set', executionId: current.executionId, exerciseId: exercise.id, body, uuid: body.clientUuid, at: Date.now() });
    return { ok: true, setIndex, body };
  }, [enqueue]);

  const completeExercise = useCallback((exerciseIdx) => {
    const current = applyQueue(serverRef.current, queueRef.current);
    const exercise = current?.exercises[exerciseIdx];
    if (!exercise || exercise.status === 'Completed' || exercise.status === 'Skipped' || (exercise.sets || []).length === 0) return false;
    enqueue({ type: 'complete-exercise', executionId: current.executionId, exerciseId: exercise.id, at: Date.now() });
    return true;
  }, [enqueue]);

  const skipExercise = useCallback((exerciseIdx, reason) => {
    const current = applyQueue(serverRef.current, queueRef.current);
    const exercise = current?.exercises[exerciseIdx];
    if (!exercise || exercise.status === 'Completed' || exercise.status === 'Skipped' || (exercise.sets || []).length > 0) return false;
    enqueue({ type: 'skip-exercise', executionId: current.executionId, exerciseId: exercise.id, reason: reason || null, at: Date.now() });
    return true;
  }, [enqueue]);

  /**
   * Corrige ou apaga uma série JÁ CONFIRMADA pelo servidor (tem Id). Exige ligação e a fila vazia — o que ainda está na fila não tem Id e
   * corrige-se repetindo a série. Devolve { ok } ou { ok:false, message }.
   */
  const editSet = useCallback(async (exerciseIdx, set, change) => {
    const exercise = applyQueue(serverRef.current, queueRef.current)?.exercises[exerciseIdx];
    if (!exercise || !set?.id || set.pending) return { ok: false, message: 'Esta série ainda não foi enviada: aguarde um instante.' };
    setBusyKey('editSet', true);
    try {
      await flush();
      if (queueRef.current.length > 0) return { ok: false, message: 'Há séries por sincronizar. Volte a tentar quando houver ligação.' };
      const id = serverRef.current.executionId;
      const res = change === 'delete'
        ? await api.deleteSet(id, exercise.id, set.id)
        : await api.updateSet(id, exercise.id, set.id, change);
      putServer(change === 'delete' ? removeSavedSet(serverRef.current, res.data) : mergeSavedSet(serverRef.current, res.data));
      return { ok: true };
    } catch (e) {
      return { ok: false, message: errorMessage(e, change === 'delete' ? 'Não foi possível apagar a série.' : 'Não foi possível corrigir a série.') };
    } finally {
      setBusyKey('editSet', false);
    }
  }, [api, flush, putServer, setBusyKey]);

  /** Troca o exercício só nesta sessão. Exige ligação (e a fila vazia): devolve { ok } ou { ok:false, message }. */
  const substitute = useCallback(async (exerciseIdx, libraryExerciseId, reason) => {
    const exercise = applyQueue(serverRef.current, queueRef.current)?.exercises[exerciseIdx];
    if (!exercise) return { ok: false, message: 'Exercício não encontrado.' };
    setBusyKey('substitute', true);
    try {
      await flush();
      if (queueRef.current.length > 0) return { ok: false, message: 'Há séries por sincronizar. Volte a tentar quando houver ligação.' };
      const res = await api.substitute(serverRef.current.executionId, exercise.id, libraryExerciseId, reason);
      putServer({ ...res.data, resumed: serverRef.current.resumed });
      return { ok: true };
    } catch (e) {
      return { ok: false, message: errorMessage(e, 'Não foi possível substituir o exercício.') };
    } finally {
      setBusyKey('substitute', false);
    }
  }, [api, flush, putServer, setBusyKey]);

  /**
   * Guarda o «como foi este exercício?» (more | less | never). Exige ligação — a resposta é uma só e não se repete
   * por engano (o servidor substitui a anterior). Não bloqueia nada: devolve { ok } ou { ok:false, message }.
   */
  const sendFeedback = useCallback(async (exerciseIdx, verdict) => {
    const exercise = applyQueue(serverRef.current, queueRef.current)?.exercises[exerciseIdx];
    if (!exercise) return { ok: false, message: 'Exercício não encontrado.' };
    setBusyKey('feedback', true);
    try {
      const res = await api.feedback(serverRef.current.executionId, exercise.id, verdict);
      putServer({ ...res.data, resumed: serverRef.current.resumed });
      return { ok: true };
    } catch (e) {
      return { ok: false, message: errorMessage(e, 'Não foi possível guardar a resposta.') };
    } finally {
      setBusyKey('feedback', false);
    }
  }, [api, putServer, setBusyKey]);

  /** Acrescenta ao treino um exercício que o plano não trazia (fica no fim, sem prescrição). Devolve também o índice dele. */
  const addExercise = useCallback(async (libraryExerciseId) => {
    setBusyKey('addExercise', true);
    try {
      await flush();
      if (queueRef.current.length > 0) return { ok: false, message: 'Há séries por sincronizar. Volte a tentar quando houver ligação.' };
      const res = await api.addExercise(serverRef.current.executionId, libraryExerciseId);
      putServer({ ...res.data, resumed: serverRef.current.resumed });
      return { ok: true, index: (res.data.exercises || []).length - 1 };
    } catch (e) {
      return { ok: false, message: errorMessage(e, 'Não foi possível acrescentar o exercício.') };
    } finally {
      setBusyKey('addExercise', false);
    }
  }, [api, flush, putServer, setBusyKey]);

  /** Conclui o treino (depois de entregar tudo o que está na fila). */
  const finish = useCallback(async (notes) => {
    if (busy.finish) return { ok: false, message: 'Já a concluir…' };
    setBusyKey('finish', true);
    try {
      await flush();
      if (queueRef.current.length > 0) return { ok: false, message: `Há ${queueRef.current.length} registo(s) por sincronizar. Ligue-se à internet e tente outra vez.` };
      const id = serverRef.current.executionId;
      const res = await api.complete(id, notes);
      clearLocal(id);
      if (mounted.current) { setFinished(res.data); setPhase('finished'); }
      return { ok: true, data: res.data };
    } catch (e) {
      return { ok: false, message: errorMessage(e, 'Não foi possível concluir o treino.') };
    } finally {
      setBusyKey('finish', false);
    }
  }, [api, busy.finish, clearLocal, flush, setBusyKey]);

  const abandon = useCallback(async (reason) => {
    if (busy.abandon) return { ok: false };
    setBusyKey('abandon', true);
    try {
      await Promise.race([flush(), new Promise((resolve) => setTimeout(resolve, 4000))]);   // o que der para entregar, entrega-se; o resto não bloqueia
      const id = serverRef.current.executionId;
      await api.abandon(id, reason);
      clearLocal(id);
      putQueue([]);
      await load();
      return { ok: true };
    } catch (e) {
      return { ok: false, message: errorMessage(e, 'Não foi possível abandonar o treino.') };
    } finally {
      setBusyKey('abandon', false);
    }
  }, [api, busy.abandon, clearLocal, flush, load, putQueue, setBusyKey]);

  /** Sai do player SEM mexer no treino: fica em curso e retoma-se mais tarde (o ecrã de hoje mostra "Continuar treino"). */
  const toToday = useCallback(() => { flush(); setPhase('today'); }, [flush]);

  /** Do ecrã de hoje para o player, num treino que já está em curso. */
  const resume = useCallback(() => { if (serverRef.current) setPhase('player'); }, []);

  const backToToday = useCallback(() => { setFinished(null); load(); }, [load]);

  const dismissFailed = useCallback((id) => setFailedOps((f) => f.filter((x) => x.id !== id)), []);

  return {
    phase, today, view, index, setIndex, finished, error, sync, failedOps, busy,
    pendingCount: queue.length,
    active: !!server && server.state === 'InProgress',
    load, start, resume, toToday, logSet, editSet, completeExercise, skipExercise, substitute, addExercise, sendFeedback, finish, abandon, backToToday, dismissFailed, flush, refresh,
  };
}
